import { z } from "zod";
import { Language, UserRole, ConsultationStatus, ReviewStatus, Gender, AuditStatus, DepartmentPosition, EpisodeStage } from "./enums";
import {
  MagicLinkRequestSchema,
  CreateUserSchema,
  UpdateUserSchema,
  CreateConsultationSchema,
  AddTranscriptionSchema,
  SummarizeRequestSchema,
  DynamicSummarizeRequestSchema,
  TranslateRequestSchema,
  KzSoapRequestSchema,
  CreatePatientSchema,
  UpdatePatientSchema,
  SharePatientSchema,
  TemplateFieldSchema,
  TemplateSnapshotSchema,
  LocalizedTextSchema,
  I18nStringSchema,
  CreateSpecialitySchema,
  UpdateSpecialitySchema,
  CreateReportTemplateSchema,
  UpdateReportTemplateSchema,
  ReportSectionSchema,
  CreateReportStructureSchema,
  UpdateReportStructureSchema,
  ReportStructureSnapshotSchema,
  NarrateReportRequestSchema,
  PatchReportSchema,
  CreateDepartmentSchema,
  UpdateDepartmentSchema,
  CreateEpisodeSchema,
  UpdateEpisodeSchema,
  AssignEpisodeSchema,
  CreatePatientDiarySchema,
  UpdatePatientDiarySchema,
  MedicalProfileItemSchema,
  UpsertMedicalProfileSchema,
  LinkReportSchema,
  MergeAnonymousPatientSchema,
  FieldSourcesSchema,
  DiagnosisCandidateSchema,
  ReportConfirmationsSchema,
  ConfirmReportSectionsSchema,
  SetDiagnosisSchema,
  PrimaryVisitExtractRequestSchema,
  PrimaryVisitExtractResponseSchema,
  ClinicalProtocolDTOSchema,
  ProtocolPlanRequestSchema,
  ProtocolPlanResponseSchema,
} from "./schemas";

// ─── Hospitals ───────────────────────────────────────────────────
export const HospitalDTOSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  nameRu: z.string().nullable().optional(),
  nameKz: z.string().nullable().optional(),
  isActive: z.boolean().default(true),
  createdAt: z.string(),
});
export type HospitalDTO = z.infer<typeof HospitalDTOSchema>;

// ─── Generic API wrapper ─────────────────────────────────────────
export const ApiResponseSchema = <T extends z.ZodTypeAny>(dataSchema: T) =>
  z.object({
    success: z.boolean(),
    data: dataSchema.optional(),
    error: z.string().optional(),
    message: z.string().optional(),
  });

export const PaginatedResponseSchema = <T extends z.ZodTypeAny>(
  itemSchema: T,
) =>
  z.object({
    items: z.array(itemSchema),
    total: z.number(),
    page: z.number(),
    pageSize: z.number(),
  });

// ─── Auth ────────────────────────────────────────────────────────
export type MagicLinkRequest = z.infer<typeof MagicLinkRequestSchema>;

export const MagicLinkResponseSchema = z.object({ message: z.string() });
export type MagicLinkResponse = z.infer<typeof MagicLinkResponseSchema>;

// ─── Users ───────────────────────────────────────────────────────
export type CreateUserInput = z.infer<typeof CreateUserSchema>;
export type UpdateUserInput = z.infer<typeof UpdateUserSchema>;

export const UserDTOSchema = z.object({
  id: z.string(),
  authId: z.string(),
  hospitalId: z.string().uuid(),
  email: z.string().email(),
  name: z.string(),
  role: z.nativeEnum(UserRole).optional().default(UserRole.Doctor),
  isActive: z.boolean().default(true),
  specialityId: z.string().uuid().nullable().optional(),
  departmentId: z.string().uuid().nullable().optional(),
  departmentPosition: z.nativeEnum(DepartmentPosition).nullable().optional(),
  createdAt: z.string(),
  hospital: HospitalDTOSchema.pick({ name: true, nameRu: true, nameKz: true })
    .optional(),
});
export type UserDTO = z.infer<typeof UserDTOSchema>;

// ─── Patients ────────────────────────────────────────────────────
export type CreatePatientInput = z.infer<typeof CreatePatientSchema>;
export type UpdatePatientInput = z.infer<typeof UpdatePatientSchema>;
export type SharePatientInput = z.infer<typeof SharePatientSchema>;

export const PatientDTOSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().nullable(),
  name: z.string(),
  personalNumber: z.string(),
  gender: z.nativeEnum(Gender),
  phone: z.string().nullable(),
  email: z.string().email().nullable(),
  dateOfBirth: z.string().nullable(),
  registeredById: z.string(),
  createdAt: z.string(),
});
export type PatientDTO = z.infer<typeof PatientDTOSchema>;

export const PatientAccessDTOSchema = z.object({
  patientId: z.string().uuid(),
  doctorId: z.string(),
  grantedById: z.string(),
  createdAt: z.string(),
});
export type PatientAccessDTO = z.infer<typeof PatientAccessDTOSchema>;

// ─── Template fields ─────────────────────────────────────────────
export type LocalizedText = z.infer<typeof LocalizedTextSchema>;
export type TemplateField = z.infer<typeof TemplateFieldSchema>;
export type TemplateSnapshot = z.infer<typeof TemplateSnapshotSchema>;
export type I18nString = z.infer<typeof I18nStringSchema>;

// ─── Departments ─────────────────────────────────────────────────
export type CreateDepartmentInput = z.infer<typeof CreateDepartmentSchema>;
export type UpdateDepartmentInput = z.infer<typeof UpdateDepartmentSchema>;

export const DepartmentDTOSchema = z.object({
  id: z.string().uuid(),
  hospitalId: z.string().uuid(),
  name: z.string(),
  description: z.string().nullable(),
  parentId: z.string().uuid().nullable(),
  createdAt: z.string(),
});
export type DepartmentDTO = z.infer<typeof DepartmentDTOSchema>;

// ─── Specialities ─────────────────────────────────────────────────
export type CreateSpecialityInput = z.infer<typeof CreateSpecialitySchema>;
export type UpdateSpecialityInput = z.infer<typeof UpdateSpecialitySchema>;

export const SpecialityDTOSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  nameRu: z.string().nullable().optional(),
  nameKz: z.string().nullable().optional(),
  isGeneral: z.boolean().default(false),
  createdAt: z.string(),
});
export type SpecialityDTO = z.infer<typeof SpecialityDTOSchema>;

// ─── Report Templates ─────────────────────────────────────────────
export type CreateReportTemplateInput = z.infer<
  typeof CreateReportTemplateSchema
>;
export type UpdateReportTemplateInput = z.infer<
  typeof UpdateReportTemplateSchema
>;

export const ReportTemplateDTOSchema = z.object({
  id: z.string().uuid(),
  specialityId: z.string().uuid(),
  name: z.string(),
  nameRu: z.string().nullable().optional(),
  nameKz: z.string().nullable().optional(),
  version: z.number(),
  isActive: z.boolean(),
  isGeneral: z.boolean().default(false),
  // The primary visit — the questionnaire a doctor is never asked to pick.
  isPrimaryVisit: z.boolean().default(false),
  fields: z.array(TemplateFieldSchema),
  llmPrompt: z.string().nullable(),
  mohFormId: z.string().nullable().optional(),
  reportStructureId: z.string().uuid().nullable().optional(),
  createdById: z.string(),
  createdAt: z.string(),
  // Whether the requesting user's hospital has opted out of this
  // (globally shared) template. Only populated on the list/get endpoints.
  hiddenForHospital: z.boolean().optional(),
});
export type ReportTemplateDTO = z.infer<typeof ReportTemplateDTOSchema>;

// ─── Report Structures ────────────────────────────────────────────
export type ReportSection = z.infer<typeof ReportSectionSchema>;
export type ReportStructureSnapshot = z.infer<
  typeof ReportStructureSnapshotSchema
>;
export type CreateReportStructureInput = z.infer<
  typeof CreateReportStructureSchema
>;
export type UpdateReportStructureInput = z.infer<
  typeof UpdateReportStructureSchema
>;

export const ReportStructureDTOSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  nameRu: z.string().nullable().optional(),
  nameKz: z.string().nullable().optional(),
  mohFormId: z.string().nullable().optional(),
  sections: z.array(ReportSectionSchema),
  version: z.number(),
  isActive: z.boolean(),
  isDefault: z.boolean(),
  createdById: z.string().nullable().optional(),
  createdAt: z.string(),
});
export type ReportStructureDTO = z.infer<typeof ReportStructureDTOSchema>;

// ─── Consultations ───────────────────────────────────────────────
export type CreateConsultationInput = z.infer<typeof CreateConsultationSchema>;

export const ConsultationDTOSchema = z.object({
  id: z.string().uuid(),
  doctorId: z.string(),
  doctorName: z.string().nullable().optional(),
  patientId: z.string().uuid().nullable(),
  patientName: z.string(),
  templateId: z.string().uuid().nullable(),
  language: z.nativeEnum(Language),
  status: z.nativeEnum(ConsultationStatus),
  startedAt: z.string(),
  endedAt: z.string().nullable(),
  summary: z.string().nullable().optional(),
  patientPersonalNumber: z.string().nullable().optional(),
  flaggedForAudit: z.boolean().optional(),
  completedById: z.string().uuid().nullable().optional(),
  completedByName: z.string().nullable().optional(),
  doctorSpeciality: z
    .object({
      name: z.string(),
      nameRu: z.string().nullable(),
      nameKz: z.string().nullable(),
    })
    .nullable()
    .optional(),
  templateName: z.string().nullable().optional(),
  // Localized template names, resolved client-side (the API carries no locale).
  template: z
    .object({
      name: z.string(),
      nameRu: z.string().nullable().optional(),
      nameKz: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
});
export type ConsultationDTO = z.infer<typeof ConsultationDTOSchema>;

// ─── Transcriptions ──────────────────────────────────────────────
export type AddTranscriptionInput = z.infer<typeof AddTranscriptionSchema>;

export const TranscriptionDTOSchema = z.object({
  id: z.string().uuid(),
  consultationId: z.string().uuid(),
  text: z.string(),
  isEdited: z.boolean().default(false),
  originalText: z.string().nullable().optional(),
  createdAt: z.string(),
});
export type TranscriptionDTO = z.infer<typeof TranscriptionDTOSchema>;

// ─── Reports ─────────────────────────────────────────────────────
export type PatchReportInput = z.infer<typeof PatchReportSchema>;

export const AiFindingSchema = z.object({
  field:    z.string(),
  issue:    z.string(),
  severity: z.enum(['warning', 'error']),
  fixed:    z.boolean(),
});
export type AiFinding = z.infer<typeof AiFindingSchema>;

export const ReportDTOSchema = z.object({
  id: z.string().uuid(),
  consultationId: z.string().uuid(),
  structuredData: z.record(z.unknown()),
  templateSnapshot: TemplateSnapshotSchema.nullable().optional(),
  missingFields: z.array(z.string()).nullable(),
  editedData: z.record(z.unknown()).nullable(),
  generalData: z.record(z.unknown()).nullable().optional(),
  generalEditedData: z.record(z.unknown()).nullable().optional(),
  // The report document: { [sectionKey]: paragraph }. Derived from the
  // structured answers above — never the other way round.
  narrativeData: z.record(z.string()).nullable().optional(),
  narrativeEditedData: z.record(z.string()).nullable().optional(),
  reportStructureSnapshot: ReportStructureSnapshotSchema.nullable().optional(),
  narrativeGeneratedAt: z.string().nullable().optional(),
  reviewStatus: z.nativeEnum(ReviewStatus),
  auditStatus: z.nativeEnum(AuditStatus).optional().default(AuditStatus.NotSubmitted),
  aiFindings: z.array(AiFindingSchema).nullable().optional(),
  auditorNotes: z.string().nullable().optional(),
  auditorId: z.string().uuid().nullable().optional(),
  submittedAt: z.string().nullable().optional(),
  auditedAt: z.string().nullable().optional(),
  doctorNotes: z.string().nullable().optional(),
  // Primary visit only. Where each answer came from, which parts the doctor has
  // signed off, and the diagnoses the model proposed for them to choose from.
  fieldSources: FieldSourcesSchema.nullable().optional(),
  confirmations: ReportConfirmationsSchema.nullable().optional(),
  diagnosisCandidates: z.array(DiagnosisCandidateSchema).nullable().optional(),
  createdAt: z.string(),
});
export type ReportDTO = z.infer<typeof ReportDTOSchema>;

// ─── LLM ─────────────────────────────────────────────────────────
export type SummarizeRequest = z.infer<typeof SummarizeRequestSchema>;
export type DynamicSummarizeRequest = z.infer<
  typeof DynamicSummarizeRequestSchema
>;
export type TranslateRequest = z.infer<typeof TranslateRequestSchema>;
export type KzSoapRequest = z.infer<typeof KzSoapRequestSchema>;
export type NarrateReportRequest = z.infer<typeof NarrateReportRequestSchema>;

export const NarrateReportResponseSchema = z.object({
  narrative: z.record(z.string()),
});
export type NarrateReportResponse = z.infer<typeof NarrateReportResponseSchema>;

export const SummarizeResponseSchema = z.object({
  structuredData: z.record(z.unknown()),
  rawText: z.string(),
});
export type SummarizeResponse = z.infer<typeof SummarizeResponseSchema>;

// ─── Analytics ───────────────────────────────────────────────────
export const AnalyticsSummarySchema = z.object({
  totalConsultations: z.number(),
  consultationsByLanguage: z.record(z.string(), z.number()),
  activeUsersLast30Days: z.number(),
});
export type AnalyticsSummary = z.infer<typeof AnalyticsSummarySchema>;

// ─── Episodes ─────────────────────────────────────────────────────
export type CreateEpisodeInput = z.infer<typeof CreateEpisodeSchema>;
export type UpdateEpisodeInput = z.infer<typeof UpdateEpisodeSchema>;
export type AssignEpisodeInput = z.infer<typeof AssignEpisodeSchema>;

export const EpisodeDTOSchema = z.object({
  id: z.string().uuid(),
  patientId: z.string().uuid(),
  title: z.string(),
  description: z.string().nullable(),
  stage: z.nativeEnum(EpisodeStage),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type EpisodeDTO = z.infer<typeof EpisodeDTOSchema>;

// ─── Patient Diaries ──────────────────────────────────────────────
export type CreatePatientDiaryInput = z.infer<typeof CreatePatientDiarySchema>;
export type UpdatePatientDiaryInput = z.infer<typeof UpdatePatientDiarySchema>;

export const PatientDiaryDTOSchema = z.object({
  id: z.string().uuid(),
  patientId: z.string().uuid(),
  doctorId: z.string().uuid(),
  doctorName: z.string().nullable().optional(),
  title: z.string(),
  content: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type PatientDiaryDTO = z.infer<typeof PatientDiaryDTOSchema>;

// ─── Patient Medical Profile ──────────────────────────────────────
export type MedicalProfileItem = z.infer<typeof MedicalProfileItemSchema>;
export type UpsertMedicalProfileInput = z.infer<typeof UpsertMedicalProfileSchema>;

export const MedicalProfileDTOSchema = z.object({
  id: z.string().uuid(),
  patientId: z.string().uuid(),
  allergies: z.array(MedicalProfileItemSchema),
  chronicConditions: z.array(MedicalProfileItemSchema),
  surgicalHistory: z.array(MedicalProfileItemSchema),
  updatedAt: z.string(),
});
export type MedicalProfileDTO = z.infer<typeof MedicalProfileDTOSchema>;

// ─── Report linking ───────────────────────────────────────────────
export type LinkReportInput = z.infer<typeof LinkReportSchema>;

// ─── Merge anonymous patient ──────────────────────────────────────
export type MergeAnonymousPatientInput = z.infer<typeof MergeAnonymousPatientSchema>;

// ─── Primary visit ────────────────────────────────────────────────
export type FieldSources = z.infer<typeof FieldSourcesSchema>;
export type DiagnosisCandidate = z.infer<typeof DiagnosisCandidateSchema>;
export type ReportConfirmations = z.infer<typeof ReportConfirmationsSchema>;
export type ConfirmReportSectionsInput = z.infer<
  typeof ConfirmReportSectionsSchema
>;
export type SetDiagnosisInput = z.infer<typeof SetDiagnosisSchema>;
export type PrimaryVisitExtractRequest = z.infer<
  typeof PrimaryVisitExtractRequestSchema
>;
export type PrimaryVisitExtractResponse = z.infer<
  typeof PrimaryVisitExtractResponseSchema
>;
export type ClinicalProtocolDTO = z.infer<typeof ClinicalProtocolDTOSchema>;
export type ProtocolPlanRequest = z.infer<typeof ProtocolPlanRequestSchema>;
export type ProtocolPlanResponse = z.infer<typeof ProtocolPlanResponseSchema>;
